import { BRANDING } from "../../utils/branding";

import { formatJalaliDate, formatJalaliTime, formatJalaliYear } from "./dateFormat";
// Modern A4 Sale Invoice Printing

const getPrintableProductName = (item, fallbackIndex) => {
  const rawName = String(
    item?.product_details?.name || `Product ${fallbackIndex + 1}`
  );
  const unitTitle = String(item?.unit_details?.title || "").trim();
  if (!unitTitle) return rawName;

  const suffix = ` - ${unitTitle}`;
  return rawName.endsWith(suffix) ? rawName.slice(0, -suffix.length) : rawName;
};

export const printSale = (sale, options = {}) => {
  const visitorName = options?.visitorName ? String(options.visitorName) : "";
  const customerBalance =
    options?.customerBalance ?? sale.customer_details?.total_owed_by_currency ?? null;
  const printWindow = window.open("", "_blank");
  const brandaranLogo = `${window.location.origin}/logo.jpeg`;

  const currencySymbol =
    sale.currency_details?.symbol || sale.currency_details?.title || "AF";

  const formatCurrency = (currencyObj) => {
    if (!currencyObj) return "0.00";
    if (typeof currencyObj === "object") {
      return String(Object.values(currencyObj)[0] || "0.00");
    }
    return String(currencyObj);
  };

  const formatBalance = (balance) => {
    if (balance == null || balance === "") {
      return `0.00 ${currencySymbol}`;
    }

    if (typeof balance === "object") {
      const entries = Object.entries(balance).filter(([, amount]) => amount != null && amount !== "");
      if (!entries.length) {
        return `0.00 ${currencySymbol}`;
      }

      return entries
        .map(([currency, amount]) => {
          const numericAmount = Number(amount);
          const formattedAmount = Number.isFinite(numericAmount)
            ? numericAmount.toFixed(2)
            : String(amount);
          return `${formattedAmount} ${currency}`;
        })
        .join(" / ");
    }

    const numericBalance = Number(balance);
    if (Number.isFinite(numericBalance)) {
      return `${numericBalance.toFixed(2)} ${currencySymbol}`;
    }

    return `${String(balance)} ${currencySymbol}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return formatJalaliDate(date);
  };

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Sale Invoice</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          font-size: 12px;
          line-height: 1.4;
          color: #333;
          background: white;
        }
        .invoice {
          max-width: 210mm;
          margin: 0 auto;
          padding: 12mm;
          background: white;
          box-shadow: 0 0 10px rgba(0,0,0,0.1);
        }
        .header {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: flex-start;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 3px solid #2196F3;
        }
        .header-spacer {
          min-height: 1px;
        }
        .company-info {
          flex: 1;
        }
        .company-info.centered {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          width: fit-content;
          justify-self: center;
        }
        .company-brand {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 6px;
        }
        .company-logos {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .company-logo {
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #d8e6f6;
          border-radius: 8px;
          background: #fff;
          padding: 4px 6px;
        }
        .company-logo img {
          display: block;
          object-fit: contain;
          width: 76px;
          height: 76px;
        }
        .company-name {
          font-size: 28px;
          font-weight: bold;
          color: #2196F3;
          margin-bottom: 5px;
          text-align: center;
        }
        .company-contact {
          color: #666;
          font-size: 14px;
          text-align: center;
        }
        .invoice-info {
          text-align: right;
          flex: 1;
          justify-self: end;
        }
        .invoice-title {
          font-size: 32px;
          font-weight: bold;
          color: #2196F3;
          margin-bottom: 10px;
        }
        .invoice-number {
          font-size: 18px;
          color: #666;
        }
        .details-section {
          display: flex;
          justify-content: space-between;
          margin-bottom: 30px;
        }
        .detail-box {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          flex: 1;
          margin: 0 10px;
        }
        .detail-box:first-child { margin-left: 0; }
        .detail-box:last-child { margin-right: 0; }
        .detail-title {
          font-weight: bold;
          color: #2196F3;
          margin-bottom: 10px;
          font-size: 16px;
        }
        .detail-content {
          color: #666;
          font-size: 14px;
        }
        .items-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 30px;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        .items-table th {
          background: #2196F3;
          color: white;
          padding: 15px;
          text-align: left;
          font-weight: 600;
        }
        .items-table td {
          padding: 15px;
          border-bottom: 1px solid #eee;
        }
        .items-table tr:hover {
          background: #f8f9fa;
        }
        .items-table tr:last-child td {
          border-bottom: none;
        }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .summary-section {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 30px;
        }
        .summary-box {
          background: #f8f9fa;
          padding: 25px;
          border-radius: 8px;
          min-width: 300px;
        }
        .summary-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 10px;
          padding: 5px 0;
        }
        .summary-row.total {
          border-top: 2px solid #2196F3;
          padding-top: 15px;
          margin-top: 15px;
          font-weight: bold;
          font-size: 18px;
          color: #2196F3;
        }
        .footer {
          text-align: center;
          margin-top: 40px;
          padding-top: 20px;
          border-top: 1px solid #eee;
          color: #666;
        }
        .thank-you {
          font-size: 18px;
          font-weight: bold;
          color: #2196F3;
          margin-bottom: 10px;
        }
        .note-section {
          margin-top: 12px;
          margin-bottom: 12px;
          padding: 10px 12px;
          background: #f8f9fa;
          border-radius: 4px;
          border-left: 3px solid #2196F3;
        }
        .note-title {
          font-weight: bold;
          color: #2196F3;
          font-size: 12px;
          margin-bottom: 4px;
        }
        .note-content {
          font-size: 11px;
          color: #333;
          line-height: 1.5;
          white-space: pre-wrap;
          word-break: break-word;
        }
@media print {
  body { margin: 0; padding: 0; font-size: 9px; }
  .invoice { 
    box-shadow: none; 
    max-width: none;
    padding: 5mm;
  }
  .header { margin-bottom: 6px; padding-bottom: 4px; }
  .details-section { margin-bottom: 6px; }
  .detail-box { padding: 4px 6px; }
  .items-table { margin-bottom: 6px; page-break-inside: avoid; }
  .items-table th, .items-table td { padding: 6px 4px; line-height: 1.3; font-size: 10px; }
  .summary-section { margin-bottom: 6px; page-break-inside: avoid; }
  .summary-box { padding: 6px 8px; }
  .note-section { margin-top: 6px; margin-bottom: 6px; padding: 4px 6px; page-break-inside: avoid; }
  .footer { margin-top: 8px; padding-top: 4px; }
  @page { 
    size: A4; 
    margin: 8mm;
  }
  
  /* Prevent page breaks within elements */
  hwithin elements */
  h1, h2, h3, h4, h5, h6, p, li, td, th, tr, div, span { 
    orphans: 3; 
    widows: 3; 
  }
  
  /* Avoid breaking tables and containers across pages */
  table, thead, tbody, th, td, tr, 
  .detail-box, 
  .summary-box, 
  .note-section,
  .items-table { 
    page-break-inside: avoid; 
  }
  
  /* Force all content to stay on one page if possible */
  .invoice {
    page-break-inside: avoid;
  }
  
  /* Ensure content doesn't overflow */
  * {
    word-wrap: break-word;
  }
  
  /* Specific font size adjustments for print */
  .company-name { font-size: 20px; }
  .company-contact { font-size: 11px; }
  .invoice-title { font-size: 24px; }
  .invoice-number { font-size: 14px; }
  .detail-title { font-size: 13px; }
  .detail-content { font-size: 11px; }
  .summary-row.total { font-size: 14px; }
  .thank-you { font-size: 14px; }
  .note-title { font-size: 10px; }
  .note-content { font-size: 9px; }
}
      </style>
    </head>
    <body>
      <div class="invoice">
        <!-- Header -->
        <div class="header">
          <div class="header-spacer" aria-hidden="true"></div>
          <div class="company-info centered">
            <div class="company-brand">
              <div class="company-logos">
                <div class="company-logo">
                  <img src="${brandaranLogo}" alt="${BRANDING.companyName} Logo" />
                </div>
              </div>
            </div>
            <div class="company-name">${BRANDING.companyName}</div>
<div class="company-contact">
               &#9742; ${BRANDING.phoneDisplay} / 0700800817<br>
               ${BRANDING.address}
             </div>
          </div>
          <div class="invoice-info">
            <div class="invoice-title">INVOICE</div>
            <div class="invoice-number">#${sale.id}</div>
</div>
              <div class="detail-content">
                <strong>${
                  sale.customer_details?.name || "Walk-in Customer"
                }</strong>
                ${
                  sale.customer_details?.contact &&
                  sale.customer_details.contact.trim()
                    ? `<br>&#9742; ${sale.customer_details.contact}`
                    : ""
                }
                ${
                  sale.customer_details?.address &&
                  sale.customer_details.address.trim()
                    ? `<br>&#128205; ${sale.customer_details.address}`
                    : ""
                }
                ${
                  sale.customer_details?.email &&
                  sale.customer_details.email.trim()
                    ? `<br>&#9993; ${sale.customer_details.email}`
                    : ""
                }
              </div>
           </div>
          <div class="detail-box">
            <div class="detail-title">Invoice Details:</div>
            <div class="detail-content">
              ${visitorName ? `<strong>Visitor:</strong> ${visitorName}<br>` : ""}
              <strong>Date:</strong> ${formatDate(sale.sale_date)}<br>
              <strong>Time:</strong> ${formatJalaliTime()}<br>
              <strong>Currency:</strong> ${currencySymbol}<br>
              <strong>Items:</strong> ${sale.sale_items?.length || 0}
            </div>
          </div>
        </div>

        <!-- Items Table -->
        <table class="items-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Product</th>
              <th class="text-center">Qty</th>
              <th class="text-center">Unit</th>
              <th class="text-right">Unit Price</th>
              <th class="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            ${
              sale.sale_items && sale.sale_items.length > 0
                ? sale.sale_items
                    .map((item, index) => {
                      const name = getPrintableProductName(item, index);
                      const qty = item.quantity;
                      const unit = item.unit_details?.title || "-";
                      const price = parseFloat(item.sale_price).toFixed(2);
                      const total = (item.quantity * item.sale_price).toFixed(
                        2,
                      );
                      return `
                  <tr>
                    <td>${index + 1}</td>
                    <td><strong>${name}</strong></td>
                    <td class="text-center">${qty}</td>
                    <td class="text-center">${unit}</td>
                    <td class="text-right">${price} ${currencySymbol}</td>
                    <td class="text-right"><strong>${total} ${currencySymbol}</strong></td>
                  </tr>
                `;
                    })
                    .join("")
                : '<tr><td colspan="6" class="text-center">No items found</td></tr>'
            }
          </tbody>
        </table>

        <!-- Summary Section -->
<div class="summary-section">
          <div class="summary-box">
            <div class="summary-row">
              <span>Subtotal:</span>
              <span>${formatCurrency(
                sale.total_amount,
              )} ${currencySymbol}</span>
            </div>
            <div class="summary-row">
              <span>Discount:</span>
              <span>${sale.amount || "0.00"} ${currencySymbol}</span>
            </div>
            <div class="summary-row">
              <span>Rent:</span>
              <span>+${sale.rent || "0.00"} ${currencySymbol}</span>
            </div>
            <div class="summary-row">
              <span>Loader:</span>
              <span>+${sale.loader || "0.00"} ${currencySymbol}</span>
            </div>
            <div class="summary-row">
              <span>Sale Remaining:</span>
              <span>${formatCurrency(
                sale.total_remained_amount,
              )} ${currencySymbol}</span>
            </div>
            <div class="summary-row">
              <span>Amount owed:</span>
              <span>${formatBalance(customerBalance != null ? customerBalance : sale.total_remained_amount)}</span>
            </div>
            <div class="summary-row">
              <span>Paid:</span>
              <span>${formatCurrency(sale.total_paid)} ${currencySymbol}</span>
            </div>
            <div class="summary-row total">
              <span>TOTAL:</span>
              <span>${formatCurrency(
                sale.effective_total_amount,
              )} ${currencySymbol}</span>
            </div>
</div>
         </div>

        ${
          sale.notes && String(sale.notes).trim()
            ? `
        <!-- Note -->
        <div class="note-section">
          <div class="note-title">Note</div>
          <div class="note-content">${String(sale.notes)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/\n/g, "<br>")}</div>
        </div>
        `
            : ""
        }

        <!-- Footer -->
        <div class="footer">
          <div class="thank-you">Thank you for your business!</div>
          <div>© ${formatJalaliYear()} ${BRANDING.companyName} - All rights reserved</div>
          <div style="margin-top:4px;font-size:10px;color:#999;"></div>
        </div>
      </div>
      




      
      <script>
        window.onload = function() {
          setTimeout(() => {
            window.print();
            window.close();
          }, 1000);
        };
      </script>
    <div style="margin-top:12px;text-align:center;font-size:11px;color:#6b7280;">Powered by Afghan Cosmos IT Solutions | www.afghancosmos.com</div></body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
};

// Thermal receipt function (unchanged)
export const printThermalReceipt = (sale) => {
  const printWindow = window.open("", "_blank");

  const currencySymbol =
    sale.currency_details?.symbol || sale.currency_details?.title || "AF";

  const formatCurrency = (currencyObj) => {
    if (!currencyObj) return "0.00";
    if (typeof currencyObj === "object") {
      return Object.values(currencyObj)[0] || "0.00";
    }
    return currencyObj;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return formatJalaliDate(date);
  };

  const thermalContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Thermal Receipt</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: 'Courier New', monospace;
          font-size: 11px;
          line-height: 1.2;
          width: 80mm;
          margin: 0;
          padding: 0;
          background: white;
        }
        .thermal-wrapper {
          width: 100%;
          text-align: center;
          padding: 5px;
        }
        .company { font-size: 14px; font-weight: bold; margin-bottom: 2px; }
        .contact { font-size: 10px; margin-bottom: 1px; }
        .title { font-size: 12px; font-weight: bold; margin: 5px 0; }
        .line { display: flex; justify-content: space-between; margin: 1px 0; font-size: 10px; }
        .dash { border-top: 1px dashed #000; margin: 3px 0; }
        .item { margin: 2px 0; font-size: 10px; }
        .center { text-align: center; }
        @media print {
          body { width: 80mm; margin: 0; padding: 0; }
          @page { size: 80mm auto; margin: 0; }
        }
      </style>
    </head>
    <body>
      <div class="thermal-wrapper">
<div class="company">${BRANDING.companyName}</div>
         <div class="contact">&#9742; ${BRANDING.phoneDisplay} / 0700800817</div>
         <div class="contact">&#128205; ${BRANDING.shortAddress || BRANDING.address}</div>
        
        <div class="title">--- SALE RECEIPT ---</div>
        
        <div class="line">
          <span>Invoice:</span>
          <span>#${sale.id}</span>
        </div>
        <div class="line">
          <span>Date:</span>
          <span>${formatDate(sale.sale_date)}</span>
        </div>
        <div class="line">
          <span>Customer:</span>
          <span>${sale.customer_details?.name || "Cash"}</span>
        </div>
        ${
          sale.customer_details?.contact && sale.customer_details.contact.trim()
            ? `
        <div class="line">
          <span>Phone:</span>
          <span>${sale.customer_details.contact}</span>
        </div>
        `
            : ""
        }
        ${
          sale.customer_details?.address && sale.customer_details.address.trim()
            ? `
        <div class="line">
          <span>Address:</span>
          <span>${sale.customer_details.address.substring(0, 35)}</span>
        </div>
        `
            : ""
        }
        
        <div class="dash"></div>
        <div class="center" style="font-weight: bold;">ITEMS</div>
        <div class="dash"></div>
        
        ${
          sale.sale_items && sale.sale_items.length > 0
            ? sale.sale_items
                .map((item, index) => {
                  const name = getPrintableProductName(item, index).substring(0, 18);
                  const qty = item.quantity;
                  const price = parseFloat(item.sale_price).toFixed(2);
                  const total = (item.quantity * item.sale_price).toFixed(2);
                  return `
              <div class="item">
                <div style="font-weight: bold;">${name}</div>
                <div class="line">
                  <span>Qty: ${qty}</span>
                  <span>${total} ${currencySymbol}</span>
                </div>
              </div>
            `;
                })
                .join("")
            : '<div class="center">No items</div>'
        }
        
        <div class="dash"></div>
        <div class="line">
          <span>Subtotal:</span>
          <span>${formatCurrency(
            sale.total_amount,
          )} ${currencySymbol}</span>
        </div>
        <div class="line">
          <span>Discount:</span>
          <span>${sale.amount || "0.00"} ${currencySymbol}</span>
        </div>
        <div class="line">
          <span>Rent:</span>
          <span>+${sale.rent || "0.00"} ${currencySymbol}</span>
        </div>
        <div class="line">
          <span>Loader:</span>
          <span>+${sale.loader || "0.00"} ${currencySymbol}</span>
        </div>
        <div class="line">
          <span>Paid:</span>
          <span>${formatCurrency(sale.total_paid)} ${currencySymbol}</span>
        </div>
        <div class="dash"></div>
        <div class="line" style="font-weight: bold;">
          <span>BALANCE:</span>
          <span>${formatCurrency(
            sale.total_remained_amount,
          )} ${currencySymbol}</span>
        </div>
        <div class="dash"></div>
        
        <div class="center" style="margin-top: 10px; font-size: 10px;">
          Thank you!<br>
          ${formatJalaliYear()} © ${BRANDING.companyName}<br>
          <span style="font-size:9px;color:#999;"></span>
        </div>
      </div>

      <script>
        window.onload = function() {
          setTimeout(() => {
            window.print();
            window.close();
          }, 500);
        };
      </script>
    <div style="margin-top:12px;text-align:center;font-size:11px;color:#6b7280;">Powered by Afghan Cosmos IT Solutions | www.afghancosmos.com</div></body>
    </html>
  `;

  printWindow.document.write(thermalContent);
  printWindow.document.close();
};

export default { printSale, printThermalReceipt };


